import { useEffect, useRef } from 'react';

import {
  failOperation,
  startOperation,
  succeedOperation,
} from '@mint-lib/datadog';

export type UserInviteFailure =
  | 'email-check'
  | 'email-taken'
  | 'assign-clients'
  | 'request';

/**
 * Ends, in the "New user" modal, the `user.invite` RUM operation that
 * "New user" (the Mint 1 header button, the Mint 2.0 table toolbar) starts
 * before opening the modal.
 *
 * - `succeed` as soon as the user is created and their clients assigned.
 * - `fail` when a step failed: the email check request, an email the backend
 *   reports as taken, the client assignment, or the create request. Call
 *   `restartIfEnded` before every check and submit: after a failure it starts
 *   a new operation described as `retry`, so each attempt is measured on its
 *   own. An email the check reports as already in the workspace is a field
 *   error the user corrects, and keeps the operation open.
 * - Unmounting while the operation is open (Cancel, the close button, a
 *   click on the overlay, leaving the page) fails it as `abandoned`.
 * - An end after an end is a no-op, e.g. the user list failing to refresh
 *   after the user was created.
 *
 * Mirrors `usePageOperation` from `@mint-lib/datadog`, except that every
 * event carries the `usersManagementAgGrid` flag: the same modal serves the
 * Mint 1 table and the Mint 2.0 one, which create users through different
 * endpoints.
 */
export const useUserInviteOperation = (usersManagementAgGrid: boolean) => {
  const isOpenRef = useRef(true);
  const context = { usersManagementAgGrid };

  useEffect(() => {
    isOpenRef.current = true;

    return () => {
      if (isOpenRef.current) {
        isOpenRef.current = false;
        failOperation('user.invite', 'abandoned', {
          context: { usersManagementAgGrid },
        });
      }
    };
  }, [usersManagementAgGrid]);

  const restartIfEnded = () => {
    if (!isOpenRef.current) {
      isOpenRef.current = true;
      startOperation('user.invite', { description: 'retry', context });
    }
  };

  const succeed = () => {
    if (!isOpenRef.current) {
      return;
    }
    isOpenRef.current = false;
    succeedOperation('user.invite', { context });
  };

  const fail = (reason: UserInviteFailure) => {
    if (!isOpenRef.current) {
      return;
    }
    isOpenRef.current = false;
    failOperation('user.invite', 'error', { context: { ...context, reason } });
  };

  return { restartIfEnded, succeed, fail };
};

export type UserInviteOperation = ReturnType<typeof useUserInviteOperation>;
